import React, { useState } from 'react';
import {
  Box,
  Text,
  Group,
  Stack,
  ActionIcon,
  Collapse,
  Tooltip,
  Badge,
  Paper,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconClock,
  IconUser,
} from '@tabler/icons-react';
import { PaymentBookingResponseModel } from '../types/PaymentModels';
import { primaryColor } from '../constants/theme';

interface PaymentHistoryCellProps {
  booking: PaymentBookingResponseModel;
  transactionType: string;
}

const statusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
    case 'completed':
      return 'green';
    case 'pending':
      return 'yellow';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
};

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  tooltip?: string;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, tooltip }) => (
  <Group
    justify="space-between"
    align="center"
    wrap="nowrap"
    py={8}
    style={{ borderBottom: '1px solid #f0f0f0' }}
  >
    <Group gap={4} align="center" style={{ flexShrink: 0 }}>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      {tooltip && (
        <Tooltip label={tooltip} multiline maw={220} withArrow>
          <IconInfoCircle
            size={13}
            color={primaryColor}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      )}
    </Group>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Text size="sm" fw={500} ta="right">
        {value}
      </Text>
    ) : (
      value
    )}
  </Group>
);

export const PaymentHistoryCell: React.FC<PaymentHistoryCellProps> = ({
  booking,
  transactionType,
}) => {
  const [expanded, setExpanded] = useState(false);

  const formatTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const end = new Date(endTime).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${start}–${end}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const amount =
    transactionType === 'Received'
      ? `£${booking.professionalPayment}`
      : `£${booking.rate + booking.bookingFee}`;
  const displayName =
    transactionType === 'Received'
      ? booking.userName
      : booking.professionalName;

  return (
    <Box w="100%" data-testid={`payment-history-cell-${booking.id}`}>
      <Group
        justify="space-between"
        align="center"
        p="sm"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
        data-testid={`payment-history-cell-header-${booking.id}`}
      >
        <Group gap="sm" align="center">
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: `${primaryColor}1A`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconUser size={20} color={primaryColor} />
          </Box>
          <Stack gap={2}>
            <Text fw={600} size="sm">
              {displayName}
            </Text>
            <Group gap={4} align="center">
              <Text size="xs" c="dimmed">
                {formatTime(booking.startTime, booking.endTime)}
              </Text>
            </Group>
          </Stack>
        </Group>

        <Group gap="sm" align="center">
          <Stack gap={4} align="flex-end">
            <Text
              fw={700}
              size="sm"
              c={transactionType === 'Received' ? 'green' : 'inherit'}
              data-testid={`payment-history-cell-amount-${booking.id}`}
            >
              {transactionType === 'Received' ? '+' : '-'}
              {amount}
            </Text>
            <Badge
              size="xs"
              radius="sm"
              color={statusColor(booking.paymentStatus)}
              variant="light"
              data-testid={`payment-history-cell-status-${booking.id}`}
            >
              {booking.paymentStatus}
            </Badge>
          </Stack>
          <ActionIcon
            variant="subtle"
            color={primaryColor}
            data-testid={`payment-history-cell-toggle-${booking.id}`}
          >
            {expanded ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </ActionIcon>
        </Group>
      </Group>

      <Collapse in={expanded}>
        <Paper
          bg="gray.0"
          p="md"
          mx="sm"
          mb="sm"
          radius="sm"
          style={{ borderTop: `2px solid ${primaryColor}` }}
          data-testid={`payment-history-cell-details-${booking.id}`}
        >
          <Stack gap={0}>
            {transactionType === 'Received' ? (
              <>
                <DetailRow label="Client" value={booking.userName} />
                <DetailRow
                  label="Session Date"
                  value={formatDate(booking.startTime)}
                />
                <DetailRow
                  label="Session Time"
                  value={formatTime(booking.startTime, booking.endTime)}
                />
                <DetailRow label="Rate" value={`£${booking.rate}`} />
                <DetailRow
                  label="Your Payment"
                  value={`£${booking.professionalPayment}`}
                />
                <DetailRow
                  label="Payment Status"
                  value={
                    <Badge
                      size="sm"
                      radius="sm"
                      color={statusColor(booking.paymentStatus)}
                      variant="light"
                    >
                      {booking.paymentStatus}
                    </Badge>
                  }
                  tooltip="Whether the participant has sent the payment"
                />
                <DetailRow
                  label="Payout Status"
                  value={
                    <Badge
                      size="sm"
                      radius="sm"
                      color={statusColor(booking.payoutStatus)}
                      variant="light"
                    >
                      {booking.payoutStatus}
                    </Badge>
                  }
                  tooltip="Whether the payment has been sent to your bank account"
                />
              </>
            ) : (
              <>
                <DetailRow
                  label="Professional"
                  value={booking.professionalName}
                />
                <DetailRow
                  label="Session Date"
                  value={formatDate(booking.startTime)}
                />
                <DetailRow
                  label="Session Time"
                  value={formatTime(booking.startTime, booking.endTime)}
                />
                <DetailRow label="Rate" value={`£${booking.rate}`} />
                <DetailRow
                  label="Booking Fee"
                  value={`£${booking.bookingFee}`}
                />
                <DetailRow
                  label="Payment Status"
                  value={
                    <Badge
                      size="sm"
                      radius="sm"
                      color={statusColor(booking.paymentStatus)}
                      variant="light"
                    >
                      {booking.paymentStatus}
                    </Badge>
                  }
                />
              </>
            )}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};
